import apiClient from '@/api/apiClient'
import {
  saveMediaBlob,
  getMediaCacheVersion,
  getAllMediaKeys,
  getAllCachedBundleMediaKeys,
  deleteMediaBlob,
  type MediaCacheVersion,
} from '@/db/viewerDb'
import type { BundleMediaAsset, GetContentBundleResponse } from '@/types/api'

export interface MediaPreloadProgress {
  total: number
  completed: number
  failed: number
  totalBytes: number
  processedBytes: number
}

export interface MediaPreloadResult {
  aborted: boolean
  quotaExceeded: boolean
}

const CONCURRENCY = 3

type MediaRef =
  | { type: 'api'; key: string; asset: BundleMediaAsset }
  | { type: 'external'; key: string }

function isQuotaExceeded(err: unknown): boolean {
  const candidates = [err, (err as { cause?: unknown } | null)?.cause]
  return candidates.some(
    (e) => typeof e === 'object' && e !== null && (e as { name?: unknown }).name === 'QuotaExceededError',
  )
}

function isUpToDate(cached: MediaCacheVersion | null, ref: MediaRef): boolean {
  if (!cached) return false
  if (ref.type === 'external') return true
  if (ref.asset.contentHash) return cached.contentHash === ref.asset.contentHash
  return !cached.contentHash && cached.updatedOn === ref.asset.updatedOn
}

function collectRefs(bundle: GetContentBundleResponse): MediaRef[] {
  const refs = new Map<string, MediaRef>()
  for (const asset of bundle.media ?? []) {
    if (!refs.has(asset.systemFileName)) refs.set(asset.systemFileName, { type: 'api', key: asset.systemFileName, asset })
  }
  for (const url of bundle.externalMedia ?? []) {
    if (!refs.has(url)) refs.set(url, { type: 'external', key: url })
  }
  return [...refs.values()]
}

async function fetchApiBlob(systemFileName: string): Promise<{ blob: Blob; mimeType: string }> {
  const response = await apiClient.get<Blob>(`/media/${encodeURIComponent(systemFileName)}?_cb=${Date.now()}`, {
    responseType: 'blob',
  })
  const blob = response.data
  const mimeType = (response.headers['content-type'] as string | undefined) ?? blob.type ?? 'application/octet-stream'
  return { blob, mimeType }
}

async function fetchExternalBlob(url: string): Promise<{ blob: Blob; mimeType: string }> {
  const separator = url.includes('?') ? '&' : '?'
  const response = await fetch(`${url}${separator}_cb=${Date.now()}`)
  if (!response.ok) throw new Error(`HTTP ${response.status} fetching ${url}`)
  const blob = await response.blob()
  const mimeType = response.headers.get('Content-Type') ?? blob.type ?? 'application/octet-stream'
  return { blob, mimeType }
}

async function processQueue<T>(
  queue: T[],
  concurrency: number,
  fn: (item: T) => Promise<void>,
  shouldStop: () => boolean,
): Promise<void> {
  let index = 0
  async function worker() {
    while (index < queue.length && !shouldStop()) {
      await fn(queue[index++])
    }
  }
  await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, worker))
}

async function evictUnreferenced(currentKeys: Set<string>): Promise<void> {
  const referenced = await getAllCachedBundleMediaKeys()
  if (!referenced) return
  const cachedKeys = await getAllMediaKeys()
  const stale = cachedKeys.filter((k) => !referenced.has(k) && !currentKeys.has(k))
  await Promise.all(stale.map((k) => deleteMediaBlob(k)))
}

/**
 * Caches every asset listed in the bundle's media and externalMedia arrays. Bundles cached
 * before those arrays existed are ignored until the next online fetch replaces them.
 */
export async function preloadMediaAssets(
  bundle: GetContentBundleResponse,
  onProgress: (p: MediaPreloadProgress) => void,
  signal?: AbortSignal,
): Promise<MediaPreloadResult> {
  const result: MediaPreloadResult = { aborted: false, quotaExceeded: false }
  if (!bundle.media && !bundle.externalMedia) return result

  const refs = collectRefs(bundle)
  const versions = await Promise.all(refs.map((ref) => getMediaCacheVersion(ref.key)))
  const sizeOf = (ref: MediaRef) => (ref.type === 'api' ? ref.asset.fileSize : 0)

  const progress: MediaPreloadProgress = {
    total: refs.length,
    completed: 0,
    failed: 0,
    totalBytes: refs.reduce((sum, ref) => sum + sizeOf(ref), 0),
    processedBytes: 0,
  }
  const workQueue: MediaRef[] = []
  refs.forEach((ref, i) => {
    if (isUpToDate(versions[i], ref)) {
      progress.completed++
      progress.processedBytes += sizeOf(ref)
    } else {
      workQueue.push(ref)
    }
  })
  if (refs.length > 0) onProgress({ ...progress })

  await processQueue(
    workQueue,
    CONCURRENCY,
    async (ref) => {
      try {
        const { blob, mimeType } =
          ref.type === 'api' ? await fetchApiBlob(ref.key) : await fetchExternalBlob(ref.key)
        if (signal?.aborted || result.quotaExceeded) return
        await saveMediaBlob(
          ref.key,
          blob,
          mimeType,
          ref.type === 'api' ? `/api/media/${ref.key}` : ref.key,
          ref.type === 'api' ? { contentHash: ref.asset.contentHash, updatedOn: ref.asset.updatedOn } : undefined,
        )
        if (ref.type === 'external') progress.totalBytes += blob.size
        progress.completed++
        progress.processedBytes += ref.type === 'api' ? ref.asset.fileSize : blob.size
      } catch (err) {
        if (isQuotaExceeded(err)) {
          result.quotaExceeded = true
          return
        }
        console.warn(`[MediaPreloader] Failed to cache ${ref.key}`, err)
        progress.failed++
        progress.processedBytes += sizeOf(ref)
      }
      onProgress({ ...progress })
    },
    () => !!signal?.aborted || result.quotaExceeded,
  )

  if (signal?.aborted) {
    result.aborted = true
    return result
  }

  await evictUnreferenced(new Set(refs.map((r) => r.key)))
  return result
}
